import { createSelector } from 'reselect'

import { selectCurrentContactId } from 'ducks/crm/contacts'
import { selectTicketsById } from 'selectors/tickets/byId/selectTicketsById'
import { selectCurrentTicketId } from 'selectors/tickets/current/selectCurrentTicketId'
import { selectAgentsById } from 'selectors/agents/base'
import { selectAccountPreferenceSortByCollaboratorCommentEnabled } from 'selectors/app/selectAccountPreferences'
import { emptyObj } from 'util/objects'
import { selectBase as selectRequests } from 'ducks/requests'
import { selectRoomsById } from 'ducks/chat/selectors/rooms'
import { selectWidgetsById } from 'ducks/widgets/selectors/widgets'
import { getRawId } from 'util/globalId'

import { CONVERSATION, ROOM } from './types'

function selectBase(state) {
  return state?.integrations?.recentConversations || emptyObj
}

export const selectRecentConversationsAndChatsForCurrentContact = createSelector(
  selectBase,
  selectCurrentContactId,
  selectCurrentTicketId,
  selectTicketsById,
  selectAgentsById,
  selectRoomsById,
  selectWidgetsById,
  selectAccountPreferenceSortByCollaboratorCommentEnabled,
  (
    base,
    contactId,
    currentTicketId,
    ticketsById,
    agentsById,
    roomsById,
    widgetsById,
    sortByCollaboratorCommentAtEnabled
  ) => {
    const recentConversations = base[contactId]
    return recentConversations?.map(
      ({
        assignee: recentAssignee,
        id,
        isRead,
        name,
        number,
        state: recentState,
        subject: recentSubject,
        type,
        updatedAt: recentUpdatedAt,
        channelId,
      }) => {
        if (type === CONVERSATION) {
          // We have to extract and merge in data from the redux
          // store to ensure optimistic updates are reflected
          const reduxTicket = ticketsById[number]
          const agent = agentsById[reduxTicket?.assignee?.id]
          const reduxState = reduxTicket?.snoozedUntil
            ? 'snoozed'
            : reduxTicket?.state
          const state = reduxTicket ? reduxState : recentState?.toLowerCase()

          const {
            latest_collaborator_comment_at: latestCollaboratorCommentAt,
            updated_at: ticketUpdatedAt,
          } =
            reduxTicket || {}

          const updatedAt =
            (sortByCollaboratorCommentAtEnabled
              ? latestCollaboratorCommentAt
              : ticketUpdatedAt) || recentUpdatedAt

          return {
            assignee: agent ? agent?.username : recentAssignee,
            isCurrent: number.toString() === currentTicketId.toString(),
            id,
            number,
            state,
            title: name || reduxTicket ? reduxTicket?.title : recentSubject,
            type,
            updatedAt,
            from: 'email',
          }
        } else if (type === ROOM) {
          let from = 'chat'

          const widgetId = getRawId(channelId)
          const { kind } = widgetsById[widgetId] || {}

          const reduxRoom = roomsById[number]
          const agent =
            agentsById[
              getRawId(reduxRoom?.agentId) || getRawId(recentAssignee?.id)
            ]
          const state = reduxRoom
            ? reduxRoom?.state
            : recentState?.toLowerCase()

          if (kind === 'facebook') {
            from = 'messenger'
          } else if (kind === 'instagram') {
            from = 'instagram'
          }

          return {
            assignee: agent?.username,
            isCurrent: number.toString() === currentTicketId.toString(),
            id,
            number,
            state: isRead === false ? 'unread' : state,
            title: name,
            type,
            updatedAt: reduxRoom?.updatedAt || recentUpdatedAt,
            from,
          }
        }
        return null
      }
    )
  }
)

export const selectIsOrWillBeLoadingRecentConversationsAndChatsForCurrentContact = createSelector(
  selectCurrentContactId,
  selectRequests,
  (contactId, requests) => {
    return (
      requests && requests[`fetchRecentConversations-${contactId}`]?.loading
    )
  }
)
